/**
 * Markdown renditions of the public pages, plus the recovery documents an
 * agent needs when it lands somewhere that does not exist.
 *
 * These are hand-written rather than generated from the React tree so the
 * markdown stays readable and stable — an agent reading it should get the
 * same facts a human gets from the rendered page.
 */

import { absoluteUrl, SITE } from "./site";
import { MACHINE_READABLE_FILES, normalisePath, PUBLIC_ROUTES } from "./routes";

export interface MarkdownDoc {
  path: string;
  title: string;
  body: string;
}

/** Features described on the landing page, reused by every surface. */
export const FEATURES = [
  {
    title: "Nashville Numbers & transpose",
    description:
      "Key in chord progressions as Nashville numbers or chord letters and toggle between them instantly. Transposing a song is just changing its key — every chart follows. Lyrics can be shown or hidden with one tap.",
  },
  {
    title: "Multiplayer chord sheets",
    description:
      "Google Docs for chord sheets. Everyone opens the same document and sees edits in realtime. Share a link, no download needed. Edits made offline sync when you reconnect.",
  },
  {
    title: "Arrangements",
    description:
      "Mark up intros, verses, choruses and instrumental sections with colour coding so nobody gets lost mid-song.",
  },
  {
    title: "Built-in metronome",
    description:
      "Every song stores its tempo, so the click is ready before you count in. No hunting for BPMs one song at a time.",
  },
  {
    title: "Drone pad",
    description:
      "A drone pad for transitions, which crossfades between keys as you move from song to song — useful when there is no second keys player.",
  },
] as const;

function linkList(
  items: readonly { path: string; title: string; description: string }[],
): string {
  return items
    .map(
      (item) =>
        `- [${item.title}](${absoluteUrl(item.path)}): ${item.description}`,
    )
    .join("\n");
}

const HOME_BODY = `# ${SITE.name} — ${SITE.tagline}

${SITE.description}

**Canonical URL:** ${absoluteUrl("/")}
**Publisher:** ${SITE.publisher}
**Price:** Free to use.

## Features

${FEATURES.map(
  (feature) => `### ${feature.title}\n\n${feature.description}`,
).join("\n\n")}

## Getting started

1. Open [${absoluteUrl("/login")}](${absoluteUrl("/login")}) and create a free account.
2. Create a setlist and add songs, or import an existing chord sheet.
3. Share the setlist link with your band — everyone sees the same chart, live.

## Machine-readable files

${linkList(MACHINE_READABLE_FILES)}

Every page listed in the sitemap also serves this markdown rendition when you
send \`Accept: text/markdown\`.
`;

const LOGIN_BODY = `# Sign in to ${SITE.name}

Create a free ${SITE.fullName} account, or sign in to an existing one.

- Email and password
- Sign in with Google
- Sign in with Apple

Accounts are free. After signing in you land on [${absoluteUrl("/setlist")}](${absoluteUrl("/setlist")}),
which holds your setlists and song library.

Back to the [product overview](${absoluteUrl("/")}).
`;

const MARKDOWN_DOCS: readonly MarkdownDoc[] = [
  { path: "/", title: `${SITE.name} — ${SITE.tagline}`, body: HOME_BODY },
  { path: "/login", title: `Sign in to ${SITE.name}`, body: LOGIN_BODY },
];

/** Markdown rendition for a path, when one exists. */
export function getMarkdownDoc(pathname: string): MarkdownDoc | undefined {
  const path = normalisePath(pathname);
  return MARKDOWN_DOCS.find((doc) => doc.path === path);
}

/**
 * Body for a 404. Short on purpose: it names the missing path, then points at
 * the places an agent can actually recover from.
 */
export function notFoundMarkdown(pathname?: string): string {
  const subject = pathname
    ? `\`${pathname}\` does not exist`
    : "The requested path does not exist";

  return `# 404 — Page not found

${subject} on ${SITE.fullName}.

## Where to look next

${linkList([...PUBLIC_ROUTES, ...MACHINE_READABLE_FILES])}

Send \`Accept: text/markdown\` to any public page to get this markdown
rendition instead of HTML.
`;
}

/** Body for a 406, listing what we are able to return. */
export function notAcceptableMarkdown(pathname: string): string {
  return `# 406 — Not Acceptable

\`${pathname}\` can be served as \`text/html\` or \`text/markdown\`, and the
request's \`Accept\` header allows neither.

Retry with \`Accept: text/markdown\` for markdown, or \`Accept: text/html\` for
the rendered page.
`;
}

/** Contents of /llms.txt, per the llmstxt.org format. */
export function llmsTxt(): string {
  return `# ${SITE.fullName}

> ${SITE.description}

${SITE.name} is a web app. It works in any modern browser and installs as a PWA
on iOS and Android. It is free to use.

## Pages

${linkList(PUBLIC_ROUTES)}

## Features

${FEATURES.map((feature) => `- **${feature.title}**: ${feature.description}`).join("\n")}

## Machine-readable files

${linkList(MACHINE_READABLE_FILES)}

## Notes

- Public pages content-negotiate markdown: send \`Accept: text/markdown\` and
  the same URL returns \`text/markdown; charset=utf-8\`.
- Paths that do not exist return HTTP 404 with a markdown body listing these
  same entry points.
- Everything under \`${absoluteUrl("/setlist")}\` requires a signed-in account
  and holds user-owned setlists, so there is nothing there to crawl.
`;
}
